import { Component, EventEmitter, Event, h, Prop, State, Watch } from '@stencil/core';
import { Unsubscribe } from 'redux';
import { closeFilters, openFilters } from '../../../../redux/actions';
import { RootState } from '../../../../redux/reducers';
import { store } from '../../../../redux/store';
import { Search, Translations } from '../../../../redux/types';
import { ButtonSimpleStyles, SortByOptions, SortDirOptions } from '../../../../types/types';
import { ClickOutside } from '../../../../utils/stencil/clickOutside';
import { trans } from '../../../../utils/trans';
import { SearchParameters } from '../../utils';

@Component({
    tag: 'rentware-search-filters',
    shadow: true,
    styleUrl: 'style.sass',
})
export class RentwareSearchFilters {
    @Prop({ mutable: true }) locale: string;
    @Prop({ mutable: true }) translations: Translations;
    @Prop({ mutable: true }) search: Search;
    @Prop({ mutable: true }) searchParameters: SearchParameters;

    @State() searchParametersToChange: SearchParameters;

    @Event() sendSearchParameters: EventEmitter;

    unsubscribe!: Unsubscribe;
    openFilters!: typeof openFilters;
    closeFilters!: typeof closeFilters;

    @ClickOutside()
    handleOutside() {
        if (!this.search.waitingForUpdate && this.search.showFilters) {
            this.closeFilters();
        }
    }

    @Watch('searchParameters')
    searchParametersChanged() {
        this.searchParametersToChange = this.searchParameters;
    }

    componentWillLoad() {
        this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
            return {
                translations: state.system.translations,
                locale: state.app.locale,
                search: state.search,
                searchParameters: state.search.searchParameters,
            };
        });

        store.mapDispatchToProps(this, {
            openFilters,
            closeFilters,
        });

        this.searchParametersToChange = this.searchParameters;
    }

    disconnectedCallback() {
        this.unsubscribe();
    }

    sendNewParameters = () => {
        this.sendSearchParameters.emit({
            newParameters: {
                ...this.searchParametersToChange,
                resultsOffset: 0,
            }
        });

        this.closeFilters();
    }

    resetParameters = () => {
        const tags = this.search.searchOptions.withTags && this.search.searchOptions.preselectTag && this.search.allTags.tags.length > 0 ?
            [this.search.allTags.tags[0]] :
            [];

        this.searchParametersToChange = {
            ...this.searchParametersToChange,
            tags: tags,
            sortBy: SortByOptions.Default,
            sortDir: SortDirOptions.Asc,
        };
    }

    handleChangeTags = (tags: string[]) => {
        this.searchParametersToChange = { ...this.searchParametersToChange, tags: tags };
    }

    handleChangeSort = (sortOptions: SearchParameters) => {
        this.searchParametersToChange = { ...this.searchParametersToChange, ...sortOptions };
    }

    get containerClass() {
        if (this.search.showFilters) {
            return "SearchFiltersContainer";
        } else {
            return "SearchFiltersContainer HiddenFilters";
        }
    }

    get headerTitle() {
        return trans('search.filter', this.locale, this.translations);
    }

    get confirmText() {
        return trans('search.filterConfirm', this.locale, this.translations);
    }

    get resetText() {
        return trans('search.filterReset', this.locale, this.translations);
    }

    get showFilters(): boolean {
        return this.search.searchOptions.withTags && this.search.allTags?.tags?.length > 0;
    }

    get showTags() {
        return this.search.allTags?.tags?.length > 1 && this.showFilters;
    }

    render() {
        if (!this.search.searchOptions || !this.searchParameters) {
            return null;
        }

        return (
            <div class={this.containerClass}>
                <div class="SearchFiltersContainerInner">
                    <div class="ContentContainer">
                        <div class="FiltersHeaderContainer">
                            <rentware-header
                                headerTitle={this.headerTitle}
                                onButtonClick={this.closeFilters}
                            />
                        </div>

                        <rentware-spacer size="M" />
                        <div class="FiltersContainer">
                            <div class="FiltersContainerInner">
                                {this.showFilters &&
                                    <rentware-search-filters-sort
                                        onSortChange={this.handleChangeSort}
                                        shownFilters={this.search.shownFilters}
                                        onFilterAccordionClick={this.openFilters}
                                        translations={this.translations}
                                        locale={this.locale}
                                        changedSortBy={this.searchParametersToChange?.sortBy}
                                        changedSortDir={this.searchParametersToChange?.sortDir}
                                        view={this.search.searchOptions.view}
                                    />
                                }

                                {this.showTags &&
                                    <rentware-search-filters-tags
                                        allTags={this.search.allTags}
                                        changedTags={this.searchParametersToChange?.tags}
                                        searchParameters={this.searchParameters}
                                        onTagsChange={this.handleChangeTags}
                                        translations={this.translations}
                                        locale={this.locale}
                                        shownFilters={this.search.shownFilters}
                                        onFilterAccordionClick={this.openFilters}
                                    />
                                }
                            </div>
                        </div>
                    </div>

                    <div class="ButtonsContainer">
                        <rentware-button-simple
                            buttonText={this.resetText}
                            buttonStyle={ButtonSimpleStyles.Filter}
                            onClick={this.resetParameters}
                            isWaiting={this.search.waitingForUpdate}
                        />

                        <rentware-button-simple
                            buttonText={this.confirmText}
                            onClick={this.sendNewParameters}
                            isWaiting={this.search.waitingForUpdate}
                        />
                    </div>
                </div>
            </div>
        )
    }
}
